import { useEffect, useState } from "react";
import { referralAPI } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Users, Gift, Copy, Share2, Sparkles, Clock, ArrowRight } from "lucide-react";

function formatUntil(iso) {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
}

export default function Referral() {
    const { lang } = useApp();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);

    const load = async () => {
        setBusy(true);
        try { setData(await referralAPI.me()); }
        catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
        finally { setBusy(false); }
    };

    useEffect(() => { load(); }, []);

    const link = data?.referral_code ? `${window.location.origin}/?ref=${data.referral_code}` : "";

    const copy = () => { navigator.clipboard.writeText(link); toast.success(lang === "fr" ? "Lien copié" : "Link copied"); };
    const share = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "Shorts Factory", text: lang === "fr" ? "Passe de 0 à chaîne monétisée en 7 jours. -30% via mon lien :" : "Zero to monetized channel in 7 days. -30% via my link:", url: link }); }
            catch { /* user cancelled */ }
        } else copy();
    };

    if (busy) return <div className="py-16 text-center text-slate-500 font-mono-sf text-xs">LOADING ···</div>;

    return (
        <div data-testid="referral-container" className="space-y-6">
            <div>
                <div className="overline">11 · Referral</div>
                <h2 className="font-display text-2xl font-black uppercase mt-1">
                    {lang === "fr" ? "Parraine, gagne du Pro." : "Refer, unlock Pro."}
                </h2>
                <p className="text-sm text-slate-400 mt-2 max-w-2xl">
                    {lang === "fr"
                        ? "Chaque ami qui achète via ton lien te crédite 30 jours de Factory Pro gratuit. Cumulable à l'infini."
                        : "Every friend who buys through your link earns you 30 days of Factory Pro. Stacks forever."}
                </p>
            </div>

            {/* Link box */}
            <div className="rounded-lg border border-[#FF4D00]/40 bg-gradient-to-br from-[#161D2A] via-[#0F141C] to-[#0B111B] p-6 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FF4D00]/10 blur-3xl" />
                <div className="relative">
                    <div className="overline">{lang === "fr" ? "Ton lien" : "Your link"}</div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 bg-[#080B10] border border-[#253046] rounded px-4 py-3 font-mono-sf text-sm text-slate-100 truncate">
                            {link}
                        </div>
                        <button data-testid="referral-copy" onClick={copy} className="px-4 py-3 rounded border border-[#253046] hover:border-[#FF4D00] text-slate-100 flex items-center justify-center gap-2 kinetic">
                            <Copy className="w-4 h-4" /> {lang === "fr" ? "Copier" : "Copy"}
                        </button>
                        <button data-testid="referral-share" onClick={share} className="px-4 py-3 rounded flame-gradient text-black font-semibold flex items-center justify-center gap-2 kinetic flame-glow">
                            <Share2 className="w-4 h-4" /> {lang === "fr" ? "Partager" : "Share"}
                        </button>
                    </div>
                    <div className="mt-3 font-mono-sf text-[10px] text-slate-500 uppercase tracking-widest">
                        Code: <span className="text-[#FF4D00]">{data?.referral_code}</span>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatCard icon={Users}     tint="text-[#0099FF]" value={data?.invited ?? 0}         label={lang === "fr" ? "Invités" : "Invited"} />
                <StatCard icon={Sparkles}  tint="text-[#FF4D00]" value={data?.converted ?? 0}       label={lang === "fr" ? "Conversions" : "Converted"} />
                <StatCard icon={Gift}      tint="text-[#10B981]" value={data?.credit_days_earned ?? 0} label={lang === "fr" ? "Jours Pro gagnés" : "Pro days earned"} />
                <StatCard icon={Clock}     tint="text-[#FFB800]" value={data?.pro_until ? formatUntil(data.pro_until) : "—"} label={lang === "fr" ? "Pro jusqu'à" : "Pro until"} muted={!data?.pro_until} />
            </div>

            {data?.pro_active && (
                <div className="rounded border border-[#10B981]/40 bg-[#10B981]/10 p-4 flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-[#10B981]" />
                    <div className="text-sm text-slate-100">
                        {lang === "fr" ? "Factory Pro actif jusqu'au" : "Factory Pro active until"}
                        <span className="ml-1 font-bold">{formatUntil(data.pro_until)}</span>
                    </div>
                </div>
            )}

            {/* Events */}
            {data?.events?.length > 0 ? (
                <div className="rounded border border-[#253046] bg-[#0B111B] p-4">
                    <div className="overline">{lang === "fr" ? "Derniers crédits" : "Recent credits"}</div>
                    <div className="mt-3 space-y-2">
                        {data.events.map((ev) => (
                            <div key={ev.id} className="flex items-center gap-3 text-sm">
                                <Gift className="w-4 h-4 text-[#10B981]" />
                                <span className="text-slate-200">+{ev.credited_days} {lang === "fr" ? "jours" : "days"}</span>
                                <span className="text-slate-500 font-mono-sf text-xs ml-auto">{(ev.created_at || "").slice(0, 10)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="rounded border border-dashed border-[#253046] bg-[#0B111B] p-6 text-center text-sm text-slate-500">
                    {lang === "fr" ? "Aucun crédit pour l'instant. Partage ton lien pour lancer la machine." : "No credits yet. Share your link to get things rolling."}
                </div>
            )}

            {/* How it works */}
            <div className="rounded border-l-2 border-[#FF4D00] bg-[#161D2A] p-4">
                <div className="overline">{lang === "fr" ? "Comment ça marche" : "How it works"}</div>
                <ol className="mt-2 text-sm text-slate-300 space-y-1 list-decimal list-inside">
                    <li>{lang === "fr" ? "Partage ton lien avec un créateur qui hésite" : "Share your link with a hesitating creator"}</li>
                    <li>{lang === "fr" ? "Il crée un compte via ton lien" : "They sign up via your link"}</li>
                    <li>{lang === "fr" ? "Dès son premier achat, tu gagnes 30 jours Factory Pro" : "On their first purchase, you earn 30 days of Factory Pro"}</li>
                    <li>{lang === "fr" ? "Cumulable — chaque nouvel acheteur = 30 jours de plus" : "Stackable — each new buyer = 30 more days"}</li>
                </ol>
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, value, label, tint, muted }) {
    return (
        <div className="bg-[#161D2A] border border-[#253046] rounded p-4">
            <Icon className={`w-4 h-4 ${tint}`} />
            <div className={`font-display text-2xl font-black mt-2 ${muted ? "text-slate-500" : "text-white"}`}>{value}</div>
            <div className="text-[10px] font-mono-sf text-slate-500 uppercase tracking-widest mt-1">{label}</div>
        </div>
    );
}
